import type { CalcStep } from "@/lib/calc/steps";
import { randomBelow, randomIntegers, MAX_RANDOM_COUNT } from "./random";

/** Fills a Uint32Array with random words. The default is crypto.getRandomValues (see random.ts); a seeded filler is NOT secure. */
export type Filler = (a: Uint32Array) => void;

/**
 * Seeded generator for reproducible lists (a teacher repeating a draw, a test fixture).
 * Seed text is hashed with cyrb128 and drives sfc32. Both are fast general-purpose generators. They are NOT cryptographically
 * secure: anyone who knows the seed can reproduce every number. Never use a seed for a raffle, a password or anything secret.
 */
export function seededFiller(seed: string): Filler {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  for (let i = 0; i < seed.length; i++) {
    const k = seed.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067); h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213); h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067); h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213); h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  let a = (h1 ^ h2 ^ h3 ^ h4) >>> 0, b = (h2 ^ h1) >>> 0, c = (h3 ^ h1) >>> 0, d = (h4 ^ h1) >>> 0;
  const next = () => {
    const t = (((a + b) | 0) + d) | 0;
    d = (d + 1) | 0; a = b ^ (b >>> 9); b = (c + (c << 3)) | 0; c = (c << 21) | (c >>> 11); c = (c + t) | 0;
    return t >>> 0;
  };
  for (let i = 0; i < 12; i++) next();
  return (arr) => { for (let i = 0; i < arr.length; i++) arr[i] = next(); };
}

/** The sentence shown next to every result about which generator made it. */
export function methodNote(seeded: boolean): string {
  return seeded
    ? "Generator: seeded (sfc32 from your seed text). The same seed gives the same numbers every time. This is NOT cryptographically secure: anyone with the seed can reproduce the list, so do not use it for raffles, passwords or anything secret. Clear the seed to use the secure generator."
    : "Generator: your browser's crypto.getRandomValues, a cryptographically secure source, with rejection sampling so every value in the range is equally likely (no modulo bias). Nothing is sent or stored; the numbers exist only on this page.";
}

// ---------- decimals ----------

export const MAX_DECIMALS = 10;

/** "12.345" -> { int: 12345n, scale: 3 }. Commas, underscores and spaces are ignored. */
export function parseDecimal(raw: string, label: string): { int: bigint; scale: number } | { error: string } {
  const t = raw.trim().replace(/[,_\s]/g, "");
  if (!t) return { error: `Enter a ${label}.` };
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(t);
  if (!m) return { error: `${label[0].toUpperCase()}${label.slice(1)} must be a number such as 12 or 0.5.` };
  const frac = m[3] ?? "";
  const v = BigInt(m[2] + frac);
  return { int: m[1] ? -v : v, scale: frac.length };
}

const ZERO = BigInt(0), ONE = BigInt(1), TEN = BigInt(10);
// No ** on BigInt: the build target is below es2016 (see random.ts).
const pow10 = (n: number) => { let r = ONE; for (let i = 0; i < n; i++) r *= TEN; return r; };
const floorDiv = (a: bigint, b: bigint) => { const q = a / b; return a % b !== ZERO && (a < ZERO) !== (b < ZERO) ? q - ONE : q; };
const ceilDiv = (a: bigint, b: bigint) => -floorDiv(-a, b);

/** Text of a scaled integer with exactly `dp` decimals: (-1234, 2) -> "-12.34". */
export function formatScaled(v: bigint, dp: number): string {
  const neg = v < ZERO, a = (neg ? -v : v).toString().padStart(dp + 1, "0");
  const body = dp ? `${a.slice(0, a.length - dp)}.${a.slice(a.length - dp)}` : a;
  return (neg && /[1-9]/.test(body) ? "-" : "") + body;
}

export type DecimalPlan =
  | { ok: true; lo: bigint; hi: bigint; dp: number; count: number; swapped: boolean; capped: boolean; trimmed: boolean; size: bigint }
  | { ok: false; error: string; field: "min" | "max" | "count" | "unique" | "dp" };

/**
 * Numbers with `dp` decimals between min and max (both ends included). The draw is over the whole grid of multiples of 10^-dp:
 * min is rounded UP and max DOWN onto that grid, and the plan says so (`trimmed`) so a result never lies outside what you typed.
 */
export function planDecimal(minRaw: string, maxRaw: string, countRaw: string, unique: boolean, dpRaw: string | number): DecimalPlan {
  const dp = typeof dpRaw === "number" ? dpRaw : Number(dpRaw.trim());
  if (!Number.isInteger(dp) || dp < 0 || dp > MAX_DECIMALS) return { ok: false, error: `Decimal places must be a whole number from 0 to ${MAX_DECIMALS}.`, field: "dp" };
  const a = parseDecimal(minRaw, "minimum");
  if ("error" in a) return { ok: false, error: a.error, field: "min" };
  const b = parseDecimal(maxRaw, "maximum");
  if ("error" in b) return { ok: false, error: b.error, field: "max" };
  const ct = parseDecimal(countRaw, "amount");
  if ("error" in ct || ct.scale > 0) return { ok: false, error: "How many must be a whole number.", field: "count" };
  if (ct.int < ONE) return { ok: false, error: "How many must be at least 1.", field: "count" };
  const swapped = a.int * pow10(b.scale) > b.int * pow10(a.scale);
  const first = swapped ? b : a, second = swapped ? a : b;
  // Onto the grid: min up, max down.
  const onGrid = (v: { int: bigint; scale: number }, up: boolean) => (v.scale <= dp ? v.int * pow10(dp - v.scale) : (up ? ceilDiv : floorDiv)(v.int, pow10(v.scale - dp)));
  const lo = onGrid(first, true), hi = onGrid(second, false);
  const trimmed = first.scale > dp || second.scale > dp;
  if (lo > hi) return { ok: false, error: `No number with ${dp} decimal place${dp === 1 ? "" : "s"} lies between ${minRaw.trim()} and ${maxRaw.trim()}. Allow more decimal places or widen the range.`, field: "max" };
  const capped = ct.int > BigInt(MAX_RANDOM_COUNT);
  const count = Number(capped ? BigInt(MAX_RANDOM_COUNT) : ct.int);
  const size = hi - lo + ONE;
  if (unique && BigInt(count) > size) {
    return { ok: false, error: `Only ${size} different value${size === ONE ? "" : "s"} with ${dp} decimal place${dp === 1 ? "" : "s"} exist between ${formatScaled(lo, dp)} and ${formatScaled(hi, dp)}, so ${count} unique numbers is not possible. Lower "How many", widen the range, allow more decimal places or turn off "No duplicates".`, field: "unique" };
  }
  return { ok: true, lo, hi, dp, count, swapped, capped, trimmed, size };
}

/** The numbers as text, each with exactly dp decimals. */
export function drawDecimals(plan: Extract<DecimalPlan, { ok: true }>, unique: boolean, sort: boolean, rand?: Filler): string[] {
  let picked = randomIntegers({ min: plan.lo, max: plan.hi, count: plan.count, unique }, rand);
  if (sort) picked = [...picked].sort((x, y) => (x < y ? -1 : x > y ? 1 : 0));
  return picked.map((v) => formatScaled(v, plan.dp));
}

/** The working behind a draw: how many values the range holds and how one was chosen. */
export function drawSteps(plan: { lo: bigint; hi: bigint; dp: number; count: number; size: bigint }, unique: boolean, seeded: boolean): CalcStep[] {
  const bits = plan.size.toString(2).length;
  return [
    { label: "Count the possible values", formula: plan.dp ? "(max - min) x 10^decimals + 1 values on the grid" : "max - min + 1", inputs: { min: formatScaled(plan.lo, plan.dp), max: formatScaled(plan.hi, plan.dp), "decimal places": plan.dp }, result: plan.size.toString() },
    { label: "Draw one position", formula: seeded ? "random 32-bit words from the seeded generator, keep the low bits, retry when the value is past the range" : "random 32-bit words from crypto.getRandomValues, keep the low bits, retry when the value is past the range (rejection sampling)", inputs: { "bits per draw": bits }, result: "every position equally likely" },
    { label: "Turn it into a number", formula: plan.dp ? "min + position / 10^decimals" : "min + position", inputs: { unique: unique ? "yes, no repeats" : "no, repeats allowed", amount: plan.count }, result: `${plan.count} number${plan.count === 1 ? "" : "s"}` },
  ];
}

// ---------- dice ----------

export const MAX_SIDES = 1000000;
export type DicePlan = { ok: true; dice: number; sides: number; modifier: number } | { ok: false; error: string };

/** "2d6", "d20", "3d8+2", "4d6-1". */
export function parseDice(raw: string): DicePlan {
  const m = /^\s*(\d*)\s*d\s*(\d+)\s*([+-]\s*\d+)?\s*$/i.exec(raw);
  if (!m) return { ok: false, error: "Write dice as NdM, such as 2d6, d20 or 3d8+2." };
  const dice = m[1] === "" ? 1 : Number(m[1]), sides = Number(m[2]), modifier = m[3] ? Number(m[3].replace(/\s/g, "")) : 0;
  if (dice < 1 || dice > MAX_RANDOM_COUNT) return { ok: false, error: `Roll between 1 and ${MAX_RANDOM_COUNT.toLocaleString("en-US")} dice at a time.` };
  if (sides < 2) return { ok: false, error: "A die needs at least 2 sides." };
  if (sides > MAX_SIDES) return { ok: false, error: `A die can have up to ${MAX_SIDES.toLocaleString("en-US")} sides.` };
  return { ok: true, dice, sides, modifier };
}

export interface DiceRoll { rolls: number[]; sum: number; total: number; text: string }
export function rollDice(p: Extract<DicePlan, { ok: true }>, rand?: Filler): DiceRoll {
  const rolls = Array.from({ length: p.dice }, () => Number(randomBelow(BigInt(p.sides), rand)) + 1);
  const sum = rolls.reduce((t, r) => t + r, 0), total = sum + p.modifier;
  const mod = p.modifier ? ` ${p.modifier > 0 ? "+" : "-"} ${Math.abs(p.modifier)}` : "";
  return { rolls, sum, total, text: `${p.dice}d${p.sides}${p.modifier ? (p.modifier > 0 ? "+" : "-") + Math.abs(p.modifier) : ""}: ${rolls.join(", ")} = ${sum}${mod}${p.modifier ? ` = ${total}` : ""}` };
}

// ---------- coin ----------

export function flipCoins(n: number, rand?: Filler): { flips: ("Heads" | "Tails")[]; heads: number; tails: number } {
  const flips = Array.from({ length: n }, () => (randomBelow(BigInt(2), rand) === ZERO ? "Heads" : "Tails") as "Heads" | "Tails");
  const heads = flips.filter((f) => f === "Heads").length;
  return { flips, heads, tails: n - heads };
}

// ---------- lottery ----------

export type LotteryPlan = { ok: true; pick: number; of: number } | { ok: false; error: string };
export function planLottery(pickRaw: string, ofRaw: string): LotteryPlan {
  const pick = Number(pickRaw.trim()), of = Number(ofRaw.trim());
  if (!Number.isInteger(of) || of < 2) return { ok: false, error: "The pool must be a whole number of at least 2." };
  if (of > 100000) return { ok: false, error: "The pool can have up to 100,000 numbers." };
  if (!Number.isInteger(pick) || pick < 1) return { ok: false, error: "Pick at least 1 number." };
  if (pick > of) return { ok: false, error: `You cannot pick ${pick} different numbers from a pool of ${of}. Lower the picks or raise the pool.` };
  return { ok: true, pick, of };
}
/** k different numbers from 1..n, sorted, as a lottery ticket. */
export function drawLottery(p: Extract<LotteryPlan, { ok: true }>, rand?: Filler): number[] {
  return randomIntegers({ min: ONE, max: BigInt(p.of), count: p.pick, unique: true }, rand).map(Number).sort((a, b) => a - b);
}
/** Odds of one specific ticket: 1 in C(n, k). Exact with BigInt. */
export function lotteryOdds(p: Extract<LotteryPlan, { ok: true }>): bigint {
  const k = Math.min(p.pick, p.of - p.pick);
  let num = ONE, den = ONE;
  for (let i = 1; i <= k; i++) { num *= BigInt(p.of - k + i); den *= BigInt(i); }
  return num / den;
}

// ---------- list ----------

export const MAX_LIST_ITEMS = 10000;
export function parseList(text: string): string[] {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}
export type ListPlan = { ok: true; items: string[]; pick: number } | { ok: false; error: string };
/** pickRaw blank or "all" means shuffle the whole list. */
export function planList(text: string, pickRaw: string): ListPlan {
  const items = parseList(text);
  if (items.length === 0) return { ok: false, error: "Paste or type your list, one item per line." };
  if (items.length > MAX_LIST_ITEMS) return { ok: false, error: `The list can have up to ${MAX_LIST_ITEMS.toLocaleString("en-US")} lines.` };
  const t = pickRaw.trim().toLowerCase();
  if (t === "" || t === "all") return { ok: true, items, pick: items.length };
  const pick = Number(t);
  if (!Number.isInteger(pick) || pick < 1) return { ok: false, error: "How many to pick must be a whole number of at least 1, or leave it empty to shuffle the whole list." };
  if (pick > items.length) return { ok: false, error: `You asked for ${pick} but the list has ${items.length} line${items.length === 1 ? "" : "s"}. Lower the number or add lines.` };
  return { ok: true, items, pick };
}
/** Partial Fisher-Yates: `pick` different lines (by position, so two identical lines are two entries), in drawn order. */
export function pickFromList(p: Extract<ListPlan, { ok: true }>, rand?: Filler): string[] {
  const order = randomIntegers({ min: ZERO, max: BigInt(p.items.length - 1), count: p.pick, unique: true }, rand);
  return order.map((i) => p.items[Number(i)]);
}
